import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useTaskCreateForm } from './useTaskCreateForm';
import { TaskCreateModalDesktop } from './TaskCreateModalDesktop';
import { ConfirmCloseModal } from './ConfirmCloseModal';
import type { TaskApiPayload } from '@/features/tasks/model/forms/useTaskForm';

interface TaskCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: TaskApiPayload) => void | Promise<void>;
  initialData?: Record<string, unknown>;
  isLoading?: boolean;
  mode?: 'create' | 'edit';
}

/**
 * Orchestrator: form logic (`useTaskCreateForm`) + одна адаптивная форма.
 * Мобильная раскладка описана в самом прототипе (порог 620px) и живёт внутри
 * `TaskCreateModalDesktop`, поэтому отдельный мобильный экран больше не нужен.
 *
 * The dialog is a card centered with auto margins inside a full-screen scroll
 * container — so it grows with its content and the whole modal scrolls as a page
 * (no inner max-height juggling, no layout jumps when content changes height).
 */
export default function TaskCreateModal({
  open,
  onClose,
  onSubmit,
  initialData,
  isLoading = false,
  mode = 'create',
}: TaskCreateModalProps) {
  const form = useTaskCreateForm({
    open,
    onClose,
    onSubmit,
    initialData,
    mode,
  });

  /* ─── Закрытие только через крестик (+ подтверждение) ───
   * Клика вне формы недостаточно — легко нажать случайно и потерять всё
   * написанное. Esc работает как крестик: не закрывает форму напрямую, а
   * открывает подтверждение; повторный Esc отменяет уже открытое подтверждение. */
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setConfirmCloseOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setConfirmCloseOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <>
      {createPortal(
        <form onSubmit={form.submit}>
          <div className="fixed inset-0 z-[9998] flex justify-center overflow-y-auto bg-black/50 px-3 py-4 lg:p-6 scrollbar-primary animate-in fade-in-0 duration-300">
            <TaskCreateModalDesktop
              form={form}
              isLoading={isLoading}
              mode={mode}
              onRequestClose={() => setConfirmCloseOpen(true)}
            />
          </div>
        </form>,
        document.body
      )}

      <ConfirmCloseModal
        open={confirmCloseOpen}
        onCancel={() => setConfirmCloseOpen(false)}
        onConfirm={() => {
          setConfirmCloseOpen(false);
          onClose();
        }}
      />
    </>
  );
}
